/*
  ExportSection.jsx
*/

// Npm.
import React, { useMemo } from 'react';

// lib.
import downloadFile from '../../../utils/downloadFile';
import graphDataToBotech from '../../../utils/graphDataToBotech';
import sanitizeFileName from '../../../utils/sanitizeFileName';

// Components.
import BottomActions from './BottomActions';
import CodeField from '../../CodeField';
import Button from '../../Button';

// Hooks.
import useThrottle from '../../../hooks/useThrottle';
import useSetPromise from '../../../hooks/useSetPromise';

/**
 * Section for exporting a model.
 */
export default function ExportSection({
  className,
  graphData,
  saveModel,
  ...rest
}) {

  const metadata = graphData._botech?.metadata || {};

  // Memo to get export text.

  const { data, error, text } = useMemo(() => {
    try {
      const data = graphDataToBotech(graphData);
      return {
        data,
        error: null,
        text: JSON.stringify(data, null, 2),
      }
    } catch (err) {
      return {
        data: null,
        error: err,
        text: JSON.stringify({ error: err.message }, null, 2),
      };
    }
  }, [graphData]);

  // Handlers.

  function handleFocusExportField(event) {
    event.target.select();
  }

  const handleClickExport = useThrottle(() => {
    const name = (metadata.name && sanitizeFileName(`${metadata.name} export.json`)) || 'botech_export.json';
    downloadFile(new Blob([text], { type: 'application/json' }), name);
  }, 500);

  // Optional save model handler.

  const [savePromise, setSavePromise] = useSetPromise();

  const handleClickSave = useThrottle(() => {
    if (saveModel) {
      const promise = saveModel(data);
      if (promise) {
        setSavePromise(promise);
      }
    }
  }, 500);

  return (
    <div className={className}>

      <p className="body2 gutterBottom">Export your model:</p>

      <div>
        <CodeField
          onFocus={handleFocusExportField}
          value={text}
          readOnly
        />
      </div>

      <BottomActions>
        {
          Boolean(saveModel) &&
          <Button onClick={handleClickSave} disabled={error != null || savePromise != null}>
            Save Model
          </Button>
        }
        <Button onClick={handleClickExport} disabled={error != null || text.length === 0}>
          Export to File
        </Button>
      </BottomActions>

    </div>
  );
}
